import { useState } from 'react';
import { FilterState } from '../types';
import './SearchFilters.css';

interface Props {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

const MODALITIES = [
  { value: '', label: 'All types' },
  { value: 'text', label: '💬 Text / Chat' },
  { value: 'code', label: '💻 Code' },
  { value: 'image-generation', label: '🖼️ Image' },
  { value: 'audio-generation', label: '🎵 Audio' },
  { value: 'video-generation', label: '🎬 Video' },
];

const BEST_FOR = [
  { value: '', label: 'Any use case' },
  { value: 'coding', label: '💻 Coding' },
  { value: 'reasoning', label: '🧠 Reasoning' },
  { value: 'math', label: '🔢 Math' },
  { value: 'tool_use', label: '🛠️ Tool Use / Agents' },
  { value: 'multilingual', label: '🌍 Multilingual' },
  { value: 'long_context', label: '📄 Long Context' },
  { value: 'creative_writing', label: '✍️ Creative Writing' },
  { value: 'analysis', label: '📊 Analysis' },
  { value: 'speed', label: '⚡ Speed' },
  { value: 'cost_efficiency', label: '💰 Cost Efficient' },
  { value: 'open_source', label: '🔓 Open Source' },
  { value: 'free_tier', label: '🆓 Free Tier' },
];

const ACCESS_CHANNELS = [
  { value: '', label: 'Any platform' },
  { value: 'web_ui', label: '🌐 Web browser' },
  { value: 'api', label: '⚡ API' },
  { value: 'cursor', label: '💻 Cursor' },
  { value: 'windsurf', label: '🏄 Windsurf' },
  { value: 'desktop_app', label: '🖥️ Desktop App' },
  { value: 'ide_plugin', label: '🔌 IDE Plugin' },
  { value: 'mobile_app', label: '📱 Mobile App' },
];

const SORT_OPTIONS = [
  { value: 'updated', label: 'Latest' },
  { value: 'score', label: '⭐ Best Score' },
  { value: 'speed', label: '⚡ Fastest' },
  { value: 'coding', label: '💻 Best Coding' },
];

const EMPTY_FILTERS: FilterState = {
  freeOnly: false,
  noDeposit: false,
  privacyFirst: false,
  searchQuery: '',
  modality: '',
  bestFor: '',
  accessChannel: '',
  sortBy: 'updated',
};

export function SearchFilters({ filters, onChange }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = (key: keyof FilterState, value: boolean | string) => {
    onChange({ ...filters, [key]: value });
  };

  const activeCount = [
    filters.freeOnly,
    filters.noDeposit,
    filters.privacyFirst,
    !!filters.modality,
    !!filters.bestFor,
    !!filters.accessChannel,
    filters.sortBy !== 'updated',
  ].filter(Boolean).length;

  return (
    <div className="filters">
      <div className="search-row">
        <input
          type="text"
          placeholder="Search models..."
          value={filters.searchQuery}
          onChange={(e) => update('searchQuery', e.target.value)}
          className="search-input"
        />
        <button
          className={`filter-toggle-btn ${showAdvanced ? 'active' : ''}`}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? '▲' : '▼'} Filters
          {activeCount > 0 && <span className="filter-count">{activeCount}</span>}
        </button>
      </div>

      {showAdvanced && (
        <div className="advanced-filters">
          {/* Access & Tier toggles */}
          <div className="filter-toggles">
            <label className={`toggle-chip ${filters.freeOnly ? 'active' : ''}`}>
              <input type="checkbox" checked={filters.freeOnly} onChange={(e) => update('freeOnly', e.target.checked)} />
              🆓 Free
            </label>
            <label className={`toggle-chip ${filters.noDeposit ? 'active' : ''}`}>
              <input type="checkbox" checked={filters.noDeposit} onChange={(e) => update('noDeposit', e.target.checked)} />
              💳 No Deposit
            </label>
            <label className={`toggle-chip ${filters.privacyFirst ? 'active' : ''}`}>
              <input type="checkbox" checked={filters.privacyFirst} onChange={(e) => update('privacyFirst', e.target.checked)} />
              🔒 Privacy
            </label>
          </div>

          <div className="filter-row">
            <label className="filter-label">Available in</label>
            <select
              className="filter-select"
              value={filters.accessChannel}
              onChange={(e) => update('accessChannel', e.target.value)}
            >
              {ACCESS_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-row">
            <label className="filter-label">Type</label>
            <select
              className="filter-select"
              value={filters.modality}
              onChange={(e) => update('modality', e.target.value)}
            >
              {MODALITIES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-row">
            <label className="filter-label">Best for</label>
            <select
              className="filter-select"
              value={filters.bestFor}
              onChange={(e) => update('bestFor', e.target.value)}
            >
              {BEST_FOR.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-row">
            <label className="filter-label">Sort by</label>
            <div className="sort-chips">
              {SORT_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  className={`sort-chip ${filters.sortBy === s.value ? 'active' : ''}`}
                  onClick={() => update('sortBy', s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {activeCount > 0 && (
            <button
              className="reset-btn"
              onClick={() => onChange(EMPTY_FILTERS)}
            >
              ✕ Reset all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
